/**
 * Circle Collections — Enterprise Component JS v2.0
 *
 * Features:
 *   1. Keyboard navigation (Arrow keys + Home/End)
 *   2. Scroll-triggered entry animations (IntersectionObserver)
 *   3. Auto / system theme detection
 *   4. Staggered animation delays
 *   5. Reduced-motion detection
 *   6. Data-driven rendering from JSON config
 *   7. Shopify theme editor live-reload support
 *   8. Responsive column detection for keyboard nav
 *
 * JSON Data Config (add to section's data-cc-config attribute):
 * {
 *   "useJsonData": true,
 *   "items": [
 *     {
 *       "title": "Boys",
 *       "image": "/path/or/url.webp",
 *       "link": "/collections/boys",
 *       "badge": "NEW",
 *       "badgePosition": "top-right",
 *       "status": "active",
 *       "cssClass": "",
 *       "style": { "--cc-item-border": "red" },
 *       "customHtml": ""
 *     }
 *   ]
 * }
 */

class CircleCollections {
    /**
     * @param {HTMLElement} section - The .cc-section root element
     */
    constructor(section) {
        this._section = section;
        this._grid = section.querySelector('[data-cc-grid]');
        this._items = [];
        this._observer = null;
        this._config = this._parseConfig();

        this._init();
    }

    /* ── Public API ────────────────────────────────────────────────────────── */

    /** Refresh items list (call after dynamic DOM changes) */
    refresh() {
        this._items = Array.from(this._section.querySelectorAll('[data-cc-item]'));
        this._applyStagger();
    }

    destroy() {
        this._observer && this._observer.disconnect();
        this._section.removeEventListener('keydown', this._keydownHandler);
    }

    /* ── Private: Init ─────────────────────────────────────────────────────── */

    _init() {
        this._initReducedMotion();
        this._initDynamicData();     // May rewrite grid HTML (must run first)
        this.refresh();              // Build this._items list
        this._initTheme();
        this._applyStagger();
        this._initScrollAnimations();
        this._initKeyboardNav();
    }

    /* ── Private: JSON Config ──────────────────────────────────────────────── */

    _parseConfig() {
        const el = this._section.querySelector('[data-cc-config]');
        if (!el) return null;
        try {
            return JSON.parse(el.textContent.trim());
        } catch (e) {
            console.warn('[CircleCollections] Invalid JSON config:', e.message);
            return null;
        }
    }

    /* ── Private: Dynamic Data Rendering ──────────────────────────────────── */

    _initDynamicData() {
        if (!this._config || !this._config.useJsonData || !Array.isArray(this._config.items)) return;
        if (!this._grid) return;

        this._grid.innerHTML = '';
        this._config.items.forEach((item, i) => {
            if (item.status === 'hidden') return;
            this._grid.appendChild(this._createItemEl(item, i));
        });
    }

    /**
     * Build a complete .cc-item <li> from a plain JS object.
     * @param {Object} item
     * @param {number} index
     * @returns {HTMLLIElement}
     */
    _createItemEl(item, index) {
        const li = document.createElement('li');
        const disabled = item.status === 'disabled';
        const hoverCls = item.hoverEffect ? `cc-hover--${item.hoverEffect}` : '';
        const animCls = item.animation ? `cc-anim--${item.animation}` : '';

        li.className = ['cc-item', item.cssClass || '', disabled ? 'cc-item--disabled' : '', hoverCls, animCls]
            .filter(Boolean).join(' ');
        li.setAttribute('data-cc-item', '');

        if (item.style && typeof item.style === 'object') {
            Object.entries(item.style).forEach(([k, v]) => li.style.setProperty(k, v));
        }

        const badge = item.badge
            ? `<span class="cc-badge cc-badge--${item.badgePosition || 'top-right'}" role="note">${item.badge}</span>`
            : '';

        const img = item.image
            ? `<img src="${this._esc(item.image)}" alt="${this._esc(item.title || '')}" class="cc-item__img" loading="lazy" decoding="async">`
            : `<div class="cc-item__placeholder"></div>`;

        li.innerHTML = `
      <a
        href="${this._esc(item.link || '#')}"
        class="cc-item__link"
        aria-label="${this._esc(item.title || '')}"
        ${disabled ? 'aria-disabled="true" tabindex="-1"' : ''}
      >
        <div class="cc-item__media-outer">
          <div class="cc-item__media cc-img-mode--${this._esc(item.imageMode || 'cover')}">
            ${img}
            ${item.customHtml || ''}
          </div>
          ${badge}
        </div>
        ${item.title ? `<span class="cc-item__title">${this._esc(item.title)}</span>` : ''}
      </a>
    `;

        return li;
    }

    /* ── Private: Stagger Delays ───────────────────────────────────────────── */

    _applyStagger() {
        this._items = Array.from(this._section.querySelectorAll('[data-cc-item]'));
        const baseDelay = parseInt(
            this._section.dataset.ccStagger || getComputedStyle(this._section).getPropertyValue('--cc-stagger-base') || '60'
        );
        this._items.forEach((item, i) => {
            if (!item.style.getPropertyValue('--cc-anim-delay')) {
                item.style.setProperty('--cc-anim-delay', `${i * baseDelay}ms`);
            }
        });
    }

    /* ── Private: Scroll Animations ────────────────────────────────────────── */

    _initScrollAnimations() {
        const animated = this._items.filter(el =>
            /cc-anim--(?!pending|visible)/.test(el.className)
        );
        if (!animated.length) return;

        if (!('IntersectionObserver' in window)) {
            animated.forEach(el => el.classList.add('cc-anim--visible'));
            return;
        }

        this._observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return;
                    entry.target.classList.add('cc-anim--visible');
                    this._observer.unobserve(entry.target);
                });
            },
            { threshold: 0.08, rootMargin: '0px 0px -10% 0px' }
        );

        animated.forEach(el => {
            el.classList.add('cc-anim--pending');
            this._observer.observe(el);
        });
    }

    /* ── Private: Keyboard Navigation ─────────────────────────────────────── */

    _initKeyboardNav() {
        this._keydownHandler = this._onKeydown.bind(this);
        this._section.addEventListener('keydown', this._keydownHandler);
    }

    _onKeydown(e) {
        const focused = document.activeElement.closest('[data-cc-item]');
        if (!focused) return;

        const visible = this._items.filter(el =>
            !el.classList.contains('cc-item--hidden') &&
            !el.classList.contains('cc-item--disabled')
        );
        const idx = visible.indexOf(focused);
        if (idx === -1) return;

        const cols = this._getColumnCount();

        const targets = {
            ArrowRight: idx + 1,
            ArrowLeft: idx - 1,
            ArrowDown: idx + cols,
            ArrowUp: idx - cols,
            Home: 0,
            End: visible.length - 1,
        };

        if (!(e.key in targets)) return;
        e.preventDefault();

        const next = targets[e.key];
        if (next >= 0 && next < visible.length) {
            visible[next].querySelector('.cc-item__link')?.focus();
        }
    }

    /** Detect current responsive column count from CSS grid */
    _getColumnCount() {
        if (!this._grid) return 8;
        const style = getComputedStyle(this._grid);
        const cols = style.gridTemplateColumns.split(' ').length;
        return cols || 8;
    }

    /* ── Private: Theme ────────────────────────────────────────────────────── */

    _initTheme() {
        const theme = this._section.dataset.ccTheme;
        if (theme !== 'auto') return;

        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const apply = (dark) => {
            this._section.classList.toggle('cc-theme--dark', dark);
            this._section.classList.toggle('cc-theme--light', !dark);
        };

        apply(mq.matches);
        mq.addEventListener('change', e => apply(e.matches));
    }

    /* ── Private: Reduced Motion ───────────────────────────────────────────── */

    _initReducedMotion() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this._section.classList.add('cc-motion--reduced');
        }
    }

    /* ── Private: Utilities ────────────────────────────────────────────────── */

    _esc(str) {
        const div = document.createElement('div');
        div.textContent = String(str);
        return div.innerHTML;
    }
}

/* ── Auto-init ─────────────────────────────────────────────────────────────── */

function initCircleCollections() {
    document.querySelectorAll('.cc-section').forEach(section => {
        if (!section.__ccInstance) {
            section.__ccInstance = new CircleCollections(section);
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCircleCollections);
} else {
    initCircleCollections();
}

/* ── Shopify Theme Editor live reload ──────────────────────────────────────── */
document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.cc-section');
    if (section) {
        section.__ccInstance?.destroy();
        section.__ccInstance = new CircleCollections(section);
    }
});

document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('.cc-section');
    section?.__ccInstance?.destroy();
});
